import type { Post } from "@/types";

const QUOTE_MEDIA_LS_KEY = "DEBUG_QUOTE_POST_MEDIA";

function envFlagEnabled(raw: string | undefined): boolean {
  if (raw == null || raw === "") return false;
  const v = String(raw).trim().toLowerCase();
  return v === "true" || v === "1" || v === "yes";
}

/**
 * Trace quote-post media + processing-complete refetches.
 *
 * Enable one of (dev server only; production is always off):
 * - `.env.local`: `VITE_DEBUG_QUOTE_POST_MEDIA=true` (or `1`) — restart dev server after changing
 * - Console: `localStorage.setItem('DEBUG_QUOTE_POST_MEDIA','1')` then reload
 * - Disable: `localStorage.removeItem('DEBUG_QUOTE_POST_MEDIA')` and remove the env line
 *
 * Filter console by: `[quote-post-media]`
 *
 * Root cause, mitigations, and long-term SDK work: **CLAUDE.md** → Lessons Learned →
 * “Quote posts: optimistic media vs Dexie cache”.
 */
export function isQuotePostMediaDebugEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  if (envFlagEnabled(import.meta.env.VITE_DEBUG_QUOTE_POST_MEDIA)) return true;
  try {
    const v = localStorage.getItem(QUOTE_MEDIA_LS_KEY);
    return v === "1" || v === "true";
  } catch {
    return false;
  }
}

export function debugQuotePostMedia(
  label: string,
  detail?: Record<string, unknown>,
): void {
  if (!isQuotePostMediaDebugEnabled()) return;
  if (detail !== undefined) {
    console.log(`[quote-post-media] ${label}`, detail);
  } else {
    console.log(`[quote-post-media] ${label}`);
  }
}

/** Safe one-line summary for logs (avoid huge payloads). */
export function summarizePostMedia(post: Post | null | undefined): Record<string, unknown> {
  if (!post?.ulid) {
    return { ulid: post?.ulid ?? null };
  }
  const r = post as Record<string, unknown>;
  const vu = post.videoUrls;
  const videos = r.videos as
    | Array<{ mp4?: string; hls?: string; url?: string }>
    | undefined;
  const v0 = Array.isArray(videos) ? videos[0] : undefined;
  const imgs = r.images as unknown[] | undefined;
  const img0 = Array.isArray(imgs) ? imgs[0] : undefined;
  let image0: string | undefined;
  if (typeof img0 === "string") image0 = img0.slice(0, 64);
  else if (img0 && typeof img0 === "object" && "url" in img0) {
    const u = (img0 as { url?: string }).url;
    if (typeof u === "string") image0 = u.slice(0, 64);
  }

  return {
    ulid: post.ulid,
    postType: post.postType ?? post.type,
    isProcessing: r.isProcessing,
    imageCount: Array.isArray(imgs) ? imgs.length : 0,
    image0,
    videoUrlsMp4: typeof vu?.mp4 === "string" ? vu.mp4.slice(0, 72) : vu?.mp4,
    videoUrlsHls: typeof vu?.hls === "string" ? vu.hls.slice(0, 72) : vu?.hls,
    videos0: v0
      ? {
          mp4: v0.mp4?.slice(0, 72),
          hls: v0.hls?.slice(0, 72),
          url: typeof v0.url === "string" ? v0.url.slice(0, 72) : v0.url,
        }
      : undefined,
    parentUlid: r.parentUlid,
  };
}
